// The .ts extension is explicit so node can execute this module directly (tsconfig has
// allowImportingTsExtensions). That is what keeps these utilities testable without a
// browser or a build step.
import { blockMeta, allBlockMeta } from './blockMeta.ts';

// Structural types, restated rather than imported: BlockRenderer is React, and these
// utilities must stay executable by node so they can be tested without a browser.
export interface BlockNode {
  id: string;
  component: string;
  v: number;
  props?: Record<string, unknown>;
  children?: BlockNode[];
}
export interface BlockDocument {
  schemaVersion: number;
  root: BlockNode;
}

/**
 * Structural operations on a block document.
 *
 * ── PURE, IMMUTABLE, AND TESTED DIRECTLY ─────────────────────────────────────────────
 *
 * Every function here takes a document and returns a new one. Nothing is mutated in
 * place, so React sees a new object when — and only when — something actually changed,
 * and an editor bug cannot corrupt the document somebody is still looking at.
 *
 * These are the editor's load-bearing parts, so they live apart from the React that uses
 * them and are exercised by scripts/verify-editor-document.mjs without a browser.
 *
 * ── NODE IDS ARE STABLE ──────────────────────────────────────────────────────────────
 *
 * Moving, reordering or editing a node never changes any id. Only a genuinely new node
 * gets one. That keeps diffs readable, keeps a selection valid across an edit, and means
 * a future reference to a node — an analytics event, a comment, a link — does not rot
 * because somebody dragged something.
 *
 * ── THEY REFUSE RATHER THAN REPAIR ───────────────────────────────────────────────────
 *
 * An operation that would produce an invalid document returns a refusal with a reason.
 * Creating an invalid document and relying on publish to catch it would mean the editor
 * routinely holds states the server would reject, and the admin finding out last.
 */

/**
 * What a document is edited and validated against.
 *
 * The four entity kinds plus `global_section`, which is not an entity but the inside of
 * a reusable section. Mirrors the engine's CONTEXTS, and `allowedChildren` filters the
 * palette by it — which is how an EventTickets block is kept out of a footer.
 */
export type EditorContext =
  'event' | 'venue' | 'artist' | 'standalone' | 'global_section';

export interface OperationResult {
  ok: boolean;
  document: BlockDocument;
  /** Machine-readable, for tests and for the UI to choose a message. */
  reason?: string;
  message?: string;
  /** The node the caller should now select, when an operation creates one. */
  newNodeId?: string;
}

const refuse = (
  document: BlockDocument, reason: string, message: string,
): OperationResult => ({ ok: false, document, reason, message });

// ── Reading ─────────────────────────────────────────────────────────────────

/** Depth-first, document order. */
export function walk(
  document: BlockDocument,
  visit: (node: BlockNode, parent: BlockNode | null, index: number, depth: number) => void,
): void {
  const recurse = (node: BlockNode, parent: BlockNode | null, index: number, depth: number) => {
    visit(node, parent, index, depth);
    (node.children ?? []).forEach((child, i) => recurse(child, node, i, depth + 1));
  };
  if (document?.root) recurse(document.root, null, 0, 1);
}

export function findNode(document: BlockDocument, nodeId: string): BlockNode | null {
  let found: BlockNode | null = null;
  walk(document, (node) => { if (node.id === nodeId) found = node; });
  return found;
}

export function findParent(document: BlockDocument, nodeId: string): BlockNode | null {
  let found: BlockNode | null = null;
  walk(document, (node, parent) => { if (node.id === nodeId) found = parent; });
  return found;
}

export function allNodeIds(document: BlockDocument): string[] {
  const ids: string[] = [];
  walk(document, (node) => ids.push(node.id));
  return ids;
}

export function countComponent(document: BlockDocument, component: string): number {
  let count = 0;
  walk(document, (node) => { if (node.component === component) count += 1; });
  return count;
}

export function documentDepth(document: BlockDocument): number {
  let max = 0;
  walk(document, (_n, _p, _i, depth) => { max = Math.max(max, depth); });
  return max;
}

export function nodeCount(document: BlockDocument): number {
  let count = 0;
  walk(document, () => { count += 1; });
  return count;
}

// ── Limits, mirrored from the server ────────────────────────────────────────
//
// The server is authoritative; these exist so the editor refuses at the moment of the
// action rather than at publish, when the admin has forgotten what they did.
export const EDITOR_LIMITS = { maxNodes: 500, maxDepth: 12, maxChildren: 100 };

// ── Ids ─────────────────────────────────────────────────────────────────────

/**
 * A new node id: short, readable in a diff, and unique within the document.
 *
 * Not a uuid. A document is small and human-reviewable, and `hero_3` tells somebody
 * reading a diff what they are looking at where a uuid would not.
 */
export function generateNodeId(document: BlockDocument, component: string): string {
  const base = component.replace(/[^A-Za-z0-9]/g, '').toLowerCase().slice(0, 20) || 'node';
  const taken = new Set(allNodeIds(document));
  for (let i = 1; i < 10000; i += 1) {
    const candidate = i === 1 ? base : `${base}_${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}_${Date.now()}`;
}

// ── Rules ───────────────────────────────────────────────────────────────────

/** Blocks that may be added inside this parent, in this context. */
export function allowedChildren(parent: BlockNode, context: EditorContext): string[] {
  const entry = blockMeta(parent.component);
  if (!entry || entry.children === 'none') return [];
  return allBlockMeta()
    .filter((block) => block.contexts.includes(context))
    .map((block) => block.id);
}

export function canAcceptChildren(node: BlockNode): boolean {
  return blockMeta(node.component)?.children === 'any';
}

/** How many critical blocks live in a subtree, counting the node itself. */
function criticalCountIn(node: BlockNode): number {
  let found = blockMeta(node.component)?.critical ? 1 : 0;
  for (const child of node.children ?? []) found += criticalCountIn(child);
  return found;
}

/**
 * May this node be deleted?
 *
 * The root never can — a document without a root is not a document. A critical block may
 * not be deleted while it is the only one, because the server will refuse to publish the
 * result and the page would fall back to the emergency composition. Refusing here, with a
 * reason, is better than letting somebody discover it at Publish.
 */
export function canDelete(
  document: BlockDocument, nodeId: string,
): { allowed: boolean; reason?: string; message?: string } {
  if (document.root?.id === nodeId) {
    return { allowed: false, reason: 'root', message: 'The page container cannot be removed.' };
  }
  const node = findNode(document, nodeId);
  if (!node) return { allowed: false, reason: 'not_found', message: 'That block no longer exists.' };

  const entry = blockMeta(node.component);
  if (entry?.critical && countComponent(document, node.component) <= 1) {
    return {
      allowed: false,
      reason: 'critical',
      message: `${entry.id === 'EventTickets' ? 'The ticket panel' : entry.label} is the only one `
        + 'on this page. Removing it would leave a page that looks complete and sells nothing, '
        + 'and it could not be published.',
    };
  }

  // A subtree containing the last critical block is the same problem one level up.
  const criticalInside = criticalCountIn(node);
  if (criticalInside > 0) {
    const component = 'EventTickets';
    if (countComponent(document, component) - criticalInside < 1) {
      return {
        allowed: false,
        reason: 'critical_inside',
        message: 'This contains the only ticket panel on the page. Move the ticket panel out '
          + 'first, or the page could not be published.',
      };
    }
  }

  return { allowed: true };
}

// ── Writing ─────────────────────────────────────────────────────────────────

/** Structural clone with no prototype surprises. Documents are plain JSON by definition. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Replace one node in a tree, returning a new tree. */
function mapNode(
  node: BlockNode, nodeId: string, transform: (n: BlockNode) => BlockNode,
): BlockNode {
  if (node.id === nodeId) return transform(node);
  const children = node.children ?? [];
  if (children.length === 0) return node;
  let changed = false;
  const next = children.map((child) => {
    const mapped = mapNode(child, nodeId, transform);
    if (mapped !== child) changed = true;
    return mapped;
  });
  return changed ? { ...node, children: next } : node;
}

export function updateProps(
  document: BlockDocument, nodeId: string, props: Record<string, unknown>,
): OperationResult {
  const node = findNode(document, nodeId);
  if (!node) return refuse(document, 'not_found', 'That block no longer exists.');

  // Own keys only, and never the three that make a props object dangerous. Task 8 found
  // a real prototype-chain bug and task 10 hardened ingest; the editor must not be the
  // place that reintroduces it by merging an untrusted object.
  const safe: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue;
    safe[key] = props[key];
  }

  return {
    ok: true,
    document: {
      ...document,
      root: mapNode(document.root, nodeId, (n) => ({ ...n, props: { ...n.props, ...safe } })),
    },
  };
}

export function addNode(
  document: BlockDocument,
  { parentId, component, context, index }:
  { parentId: string; component: string; context: EditorContext; index?: number },
): OperationResult {
  const parent = findNode(document, parentId);
  if (!parent) return refuse(document, 'parent_not_found', 'That container no longer exists.');

  const entry = blockMeta(component);
  if (!entry) return refuse(document, 'unknown_component', 'That block does not exist.');

  if (!canAcceptChildren(parent)) {
    return refuse(document, 'parent_accepts_no_children',
      `${blockMeta(parent.component)?.id ?? 'That block'} cannot contain other blocks.`);
  }
  if (!entry.contexts.includes(context)) {
    return refuse(document, 'wrong_context',
      'That block cannot be used on this kind of page.');
  }
  if (nodeCount(document) >= EDITOR_LIMITS.maxNodes) {
    return refuse(document, 'too_many_nodes', 'This page already has as many blocks as it can hold.');
  }
  if ((parent.children?.length ?? 0) >= EDITOR_LIMITS.maxChildren) {
    return refuse(document, 'too_many_children', 'This container is full.');
  }

  // Depth is checked against where the node would LAND, not where the tree is deepest.
  let parentDepth = 1;
  walk(document, (n, _p, _i, depth) => { if (n.id === parentId) parentDepth = depth; });
  if (parentDepth + 1 > EDITOR_LIMITS.maxDepth) {
    return refuse(document, 'too_deep', 'Blocks cannot be nested any deeper here.');
  }

  const id = generateNodeId(document, component);
  const created: BlockNode = {
    id,
    component,
    v: entry.version,
    // From the registry, so a new block starts valid rather than starting empty and
    // becoming valid once somebody fills it in.
    props: clone(defaultPropsFor(component)),
    children: [],
  };

  const at = index ?? (parent.children?.length ?? 0);
  return {
    ok: true,
    newNodeId: id,
    document: {
      ...document,
      root: mapNode(document.root, parentId, (n) => {
        const children = [...(n.children ?? [])];
        children.splice(Math.max(0, Math.min(at, children.length)), 0, created);
        return { ...n, children };
      }),
    },
  };
}

/** Defaults come from the manifest, so a new block starts valid rather than empty. */
export function defaultPropsFor(component: string): Record<string, unknown> {
  return blockMeta(component)?.defaultProps ?? {};
}

export function removeNode(document: BlockDocument, nodeId: string): OperationResult {
  const verdict = canDelete(document, nodeId);
  if (!verdict.allowed) {
    return refuse(document, verdict.reason ?? 'refused', verdict.message ?? 'That block cannot be removed.');
  }
  const parent = findParent(document, nodeId);
  if (!parent) return refuse(document, 'root', 'The page container cannot be removed.');

  return {
    ok: true,
    document: {
      ...document,
      root: mapNode(document.root, parent.id, (n) => ({
        ...n, children: (n.children ?? []).filter((c) => c.id !== nodeId),
      })),
    },
  };
}

/** Reorder within the current parent. Node ids are untouched. */
export function moveNode(
  document: BlockDocument, nodeId: string, direction: 'up' | 'down',
): OperationResult {
  const parent = findParent(document, nodeId);
  if (!parent) return refuse(document, 'root', 'The page container cannot be moved.');

  const children = parent.children ?? [];
  const from = children.findIndex((c) => c.id === nodeId);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from < 0 || to < 0 || to >= children.length) {
    return refuse(document, 'at_edge',
      `This block is already ${direction === 'up' ? 'first' : 'last'} in its container.`);
  }

  return {
    ok: true,
    document: {
      ...document,
      root: mapNode(document.root, parent.id, (n) => {
        const next = [...(n.children ?? [])];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return { ...n, children: next };
      }),
    },
  };
}

/**
 * Move a node to a different parent, preserving it and every id beneath it.
 *
 * Refuses to move a node into its own subtree, which would detach the tree from its root
 * and lose everything inside.
 */
export function reparentNode(
  document: BlockDocument, nodeId: string, newParentId: string, index?: number,
): OperationResult {
  if (nodeId === newParentId) {
    return refuse(document, 'into_self', 'A block cannot be placed inside itself.');
  }
  const node = findNode(document, nodeId);
  const newParent = findNode(document, newParentId);
  if (!node || !newParent) return refuse(document, 'not_found', 'That block no longer exists.');
  if (document.root?.id === nodeId) {
    return refuse(document, 'root', 'The page container cannot be moved.');
  }
  if (!canAcceptChildren(newParent)) {
    return refuse(document, 'parent_accepts_no_children', 'That block cannot contain other blocks.');
  }

  let inside = false;
  const check = (n: BlockNode) => {
    if (n.id === newParentId) inside = true;
    (n.children ?? []).forEach(check);
  };
  (node.children ?? []).forEach(check);
  if (inside) {
    return refuse(document, 'into_descendant', 'A block cannot be moved inside itself.');
  }

  const detached = clone(node);
  const oldParent = findParent(document, nodeId);
  if (!oldParent) return refuse(document, 'root', 'That block cannot be moved.');

  let root = mapNode(document.root, oldParent.id, (n) => ({
    ...n, children: (n.children ?? []).filter((c) => c.id !== nodeId),
  }));
  root = mapNode(root, newParentId, (n) => {
    const children = [...(n.children ?? [])];
    const at = index ?? children.length;
    children.splice(Math.max(0, Math.min(at, children.length)), 0, detached);
    return { ...n, children };
  });

  return { ok: true, document: { ...document, root } };
}

/**
 * Duplicate a node and everything under it, with fresh ids throughout.
 *
 * Reusing ids would produce a document the server refuses for duplicate_node_id, so every
 * node in the copy is renamed — not just the top one.
 */
export function duplicateNode(document: BlockDocument, nodeId: string): OperationResult {
  const node = findNode(document, nodeId);
  const parent = findParent(document, nodeId);
  if (!node || !parent) return refuse(document, 'not_found', 'That block cannot be duplicated.');

  // Copying ANY subtree that holds a ticket panel produces a second one, so this looks
  // through the whole subtree rather than at the top node alone. Checking only the top
  // node let a two-column layout carry the panel into the copy, and the result was a
  // document the publish gate refuses for duplicate_required_block — discovered at
  // Publish, after the work, rather than at the click that caused it.
  if (criticalCountIn(node) > 0) {
    const self = blockMeta(node.component)?.critical;
    return refuse(document, 'critical', self
      ? 'A page may only have one ticket panel, so it cannot be duplicated.'
      : 'This block contains the ticket panel, and a page may only have one. '
        + 'Duplicate the blocks inside it instead, or remove the panel from the copy first.');
  }

  const taken = new Set(allNodeIds(document));
  const freshen = (n: BlockNode): BlockNode => {
    const base = n.component.replace(/[^A-Za-z0-9]/g, '').toLowerCase().slice(0, 20) || 'node';
    let id = base;
    for (let i = 2; taken.has(id); i += 1) id = `${base}_${i}`;
    taken.add(id);
    return { ...clone(n), id, children: (n.children ?? []).map(freshen) };
  };
  const copy = freshen(node);

  const index = (parent.children ?? []).findIndex((c) => c.id === nodeId);
  return {
    ok: true,
    newNodeId: copy.id,
    document: {
      ...document,
      root: mapNode(document.root, parent.id, (n) => {
        const children = [...(n.children ?? [])];
        children.splice(index + 1, 0, copy);
        return { ...n, children };
      }),
    },
  };
}

// ── Validation, as the editor sees it ───────────────────────────────────────

export interface DocumentProblem {
  severity: 'error' | 'warning';
  code: string;
  message: string;
  nodeId?: string;
}

/**
 * Everything wrong with a document, right now.
 *
 * `error` is what the server will refuse to publish; `warning` is what an admin should
 * know but may legitimately be mid-way through. Shown continuously so nothing is
 * discovered for the first time by pressing Publish.
 */
export function validateForEditor(
  document: BlockDocument, context: EditorContext,
): DocumentProblem[] {
  const problems: DocumentProblem[] = [];
  const seen = new Set<string>();

  walk(document, (node, parent, _i, depth) => {
    if (seen.has(node.id)) {
      problems.push({ severity: 'error', code: 'duplicate_node_id', nodeId: node.id,
        message: `Two blocks share the id "${node.id}".` });
    }
    seen.add(node.id);

    const entry = blockMeta(node.component);
    if (!entry) {
      problems.push({ severity: 'error', code: 'unknown_component', nodeId: node.id,
        message: `"${node.component}" is not a block this version of Voleto knows.` });
      return;
    }
    if (!entry.contexts.includes(context)) {
      problems.push({ severity: 'error', code: 'wrong_context', nodeId: node.id,
        message: `${entry.label} cannot be used on this kind of page.` });
    }
    if (node.v !== entry.version) {
      problems.push({ severity: 'error', code: 'version_mismatch', nodeId: node.id,
        message: `${entry.label} is saved at version ${node.v}, but this build has version ${entry.version}.` });
    }
    if (entry.children === 'none' && (node.children?.length ?? 0) > 0) {
      problems.push({ severity: 'error', code: 'unexpected_children', nodeId: node.id,
        message: `${entry.label} cannot contain other blocks.` });
    }
    if (depth > EDITOR_LIMITS.maxDepth) {
      problems.push({ severity: 'error', code: 'too_deep', nodeId: node.id,
        message: 'Blocks are nested too deeply.' });
    }
    if (parent === null && node.id !== document.root?.id) {
      problems.push({ severity: 'error', code: 'detached', nodeId: node.id,
        message: 'This block is not attached to the page.' });
    }
  });

  if (nodeCount(document) > EDITOR_LIMITS.maxNodes) {
    problems.push({ severity: 'error', code: 'too_many_nodes',
      message: 'This page has more blocks than a page may hold.' });
  }

  // The commerce invariant, stated exactly as the server will state it.
  if (context === 'event') {
    const tickets = countComponent(document, 'EventTickets');
    if (tickets === 0) {
      problems.push({ severity: 'error', code: 'missing_required_block',
        message: 'This page has no ticket panel. It cannot be published, and it would '
          + 'look complete while selling nothing.' });
    } else if (tickets > 1) {
      problems.push({ severity: 'error', code: 'duplicate_required_block',
        message: `This page has ${tickets} ticket panels. A page may have exactly one.` });
    }
  }

  return problems;
}

export { clone as cloneDocument };
